/**
 * 에이전트의 상시 관찰(2026-10-20 사용자 요구 — 「AI 에이전트는 시작부터 끝까지 계속 뭔가 모니터링하는 게 있어야 한다」, 범위 = 앱 전체).
 *
 * **관찰은 기계가 한다 — LLM 을 부르지 않는다.** 앱이 뜬 순간부터 닫힐 때까지 일어난 일(쿼리 실패·느린 쿼리·오래 도는 쿼리·
 * 오래된 미커밋·화면 오류·작업 실패·에이전트 경고)을 모아 두고, AI 분석은 사람이 항목의 「분석 맡기기」 를 누를 때만 한다
 * (사용자 결정 2026-10-20 — 비용은 누른 만큼, 본인 계정).
 *
 * 모듈 하나가 목록을 쥐고(agentBridge 와 같은 모양) 화면 어디서나 `observe()` 로 넣고 `useObservations()` 로 본다.
 * 목록은 이 창(탭)이 살아 있는 동안만 — 저장하지 않는다(쿼리 글·오류 글이 브라우저 저장소에 남지 않게).
 * 같은 일이 짧은 시간에 되풀이되면 한 줄로 합치고 횟수만 센다(폴링 오류가 목록을 덮지 않게).
 */
import { useEffect, useState } from 'react';

const MAX_ITEMS = 60;
const MERGE_MS = 60 * 1000;          // 같은 열쇠가 1분 안에 다시 오면 한 줄로
const TEXT_MAX = 600;                // 한 항목의 글 — 모델로 갈 수 있으므로 짧게
let seq = 0;
let items = [];                      // 최신이 앞
let seenAt = Date.now();             // 사용자가 마지막으로 본 때 — 그 뒤의 것이 「새 관찰」
const listeners = new Set();

const clip = (v, n = TEXT_MAX) => {
  const s = v == null ? '' : String(v);
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
const snapshot = () => ({ items, unseen: items.filter(x => x.lastAt > seenAt).length });
const emit = () => { const s = snapshot(); listeners.forEach(fn => { try { fn(s); } catch (_e) { /* 구독자 오류는 관찰을 멈추지 않는다 */ } }); };

/**
 * 관찰 하나를 넣는다.
 * @param {{ kind: string, severity?: 'error'|'warn'|'info', title: string, detail?: string, sql?: string, key?: string, source?: string }} ev
 *   kind — sqlError · slowQuery · longRunning · uncommitted · appError · toast · agentWarning · workflow
 *   key  — 합칠 기준(없으면 kind+title)
 */
export function observe(ev) {
  try {
    if (!ev || !ev.title) return;
    const now = Date.now();
    const key = ev.key || `${ev.kind}:${ev.title}`;
    const hit = items.find(x => x.key === key && now - x.lastAt < MERGE_MS);
    if (hit) {
      items = [{ ...hit, count: hit.count + 1, lastAt: now, detail: clip(ev.detail) || hit.detail }, ...items.filter(x => x !== hit)];
    } else {
      seq += 1;
      items = [{
        id: `obs_${seq}`, key, kind: ev.kind || 'info', severity: ev.severity || 'info',
        title: clip(ev.title, 160), detail: clip(ev.detail), sql: clip(ev.sql, 4000), source: clip(ev.source, 80),
        at: now, lastAt: now, count: 1,
      }, ...items].slice(0, MAX_ITEMS);
    }
    emit();
  } catch (_e) { /* 관찰이 기능을 죽이면 안 된다 */ }
}

export function dismissObservation(id) { items = items.filter(x => x.id !== id); emit(); }
export function clearObservations() { items = []; emit(); }
/** 사용자가 관찰 목록을 봤다 — 「새 관찰」 수를 0 으로 */
export function markObservationsSeen() { seenAt = Date.now(); emit(); }
export const observationsNow = () => snapshot();

export function useObservations() {
  const [s, setS] = useState(snapshot);
  useEffect(() => {
    listeners.add(setS);
    setS(snapshot());
    return () => { listeners.delete(setS); };
  }, []);
  return s;
}

/**
 * 알림(toast)의 오류·경고를 관찰로도 남긴다 — 앱 전체의 실패가 한 길로 모인다(SQL 튜닝·품질 검사·작성·모니터링 창의 실패 알림 포함).
 * 한 번만 감싼다. 알림 자체의 동작은 그대로다.
 */
let toastWrapped = false;
export function installToastObserver(toast) {
  if (toastWrapped || !toast) return;
  toastWrapped = true;
  const wrap = (name, severity) => {
    const orig = toast[name];
    if (typeof orig !== 'function') return;
    toast[name] = (content, opts) => {
      if (typeof content === 'string' && content.trim()) {
        observe({ kind: 'toast', severity, title: content.split('\n')[0], detail: content });
      }
      return orig.call(toast, content, opts);
    };
  };
  wrap('error', 'error');
  wrap('warn', 'warn');
  wrap('warning', 'warn');
}

/** 분석 맡기기 — 에이전트에게 보낼 요청 글(사람이 누를 때만). 화면 종류 observation 으로 그 항목만 싣는다 */
export function observationScreen(item) {
  return {
    kind: 'observation',
    text: JSON.stringify({
      kind: item.kind, severity: item.severity, title: item.title, detail: item.detail,
      sql: item.sql || undefined, source: item.source || undefined, count: item.count,
      firstAt: new Date(item.at).toISOString(), lastAt: new Date(item.lastAt).toISOString(),
    }),
  };
}
